import React, { useState } from 'react';
import teacherService from '../../services/api/teacher';
import { useQuery } from 'react-query';
import { getArabicDate } from '../../services/utils/date';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  BookOpen, 
  Clock, 
  ShieldCheck, 
  ChevronLeft, 
  Sparkles, 
  PieChart, 
  CheckCircle2, 
  Calendar, 
  Zap, 
  MousePointerClick, 
  Timer, 
  Target, 
  LineChart,
  ArrowRight,
  Star,
  Layers,
  HelpCircle,
  Info
} from 'lucide-react';

const TeacherStatistics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedMetric, setSelectedMetric] = useState('all');

  // جلب الإحصائيات بناءً على الفلاتر (المنطق الأصلي كاملاً)
  const { data: statistics, isLoading } = useQuery(
    ['teacher-statistics', selectedPeriod, selectedMetric],
    () => teacherService.getTeacherStatistics()
  );

  if (isLoading && !statistics) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تحليل بيانات الأداء...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in duration-500">
        <div className="space-y-1">
          <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <BarChart3 className="text-primary-600 bg-primary-50 p-1.5 rounded-xl" size={36} />
            لوحة التحليلات المتقدمة
          </h1>
          <p className="text-gray-500 font-medium flex items-center gap-2 mr-12">
            <ShieldCheck className="text-emerald-500" size={18} />
            متابعة دقيقة لمؤشرات النمو، تفاعل الطلاب، وكفاءة المحتوى التعليمي
          </p>
        </div>

        <div className="flex items-center gap-3">
            <div className="bg-white px-5 py-2.5 rounded-2xl border border-gray-100 shadow-sm text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                <Sparkles size={14} className="text-primary-500" />
                آخر تحديث: {getArabicDate(new Date())}
            </div>
        </div>
      </div>

      {/* Advanced Filter Toolkit */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row items-center gap-6">
        <div className="flex items-center gap-3 text-gray-400 font-bold text-xs uppercase tracking-widest ml-4">
            <PieChart size={18} /> تخصيص العرض:
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
            <div className="relative">
                <select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                >
                    <option value="week">الأسبوع الحالي</option>
                    <option value="month">الشهر الحالي</option>
                    <option value="quarter">الربع سنوي الحالي</option>
                    <option value="year">السنة الدراسية الحالية</option>
                </select>
                <ChevronLeft className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 -rotate-90 pointer-events-none" size={16} />
            </div>

            <div className="relative">
                <select
                    value={selectedMetric}
                    onChange={(e) => setSelectedMetric(e.target.value)}
                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                >
                    <option value="all">جميع الإحصائيات</option>
                    <option value="students">إحصائيات الطلاب</option>
                    <option value="courses">إحصائيات الكورسات</option>
                    <option value="engagement">معدلات المشاركة</option>
                    <option value="performance">أداء المحتوى</option>
                </select>
                <ChevronLeft className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 -rotate-90 pointer-events-none" size={16} />
            </div>

            <div className="lg:col-span-2 flex items-center justify-end">
                <button className="bg-primary-50 text-primary-600 px-6 py-3.5 rounded-2xl font-black text-xs hover:bg-primary-600 hover:text-white transition-all shadow-sm flex items-center gap-2">
                    توليد تقرير شامل <ArrowRight size={14} className="rotate-180" />
                </button>
            </div>
        </div>
      </div>

      {/* Primary KPIs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'إجمالي الطلاب', value: statistics?.totalStudents || 0, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50', growth: '+15%' },
          { label: 'إجمالي الكورسات', value: statistics?.totalCourses || 0, icon: BookOpen, color: 'text-emerald-600', bg: 'bg-emerald-50', growth: '+8%' },
          { label: 'إجمالي الدروس', value: statistics?.totalLessons || 0, icon: Layers, color: 'text-purple-600', bg: 'bg-purple-50', growth: '+12%' },
          { label: 'إجمالي المشاهدات', value: statistics?.totalViews || 0, icon: TrendingUp, color: 'text-amber-500', bg: 'bg-amber-50', growth: '+5%' }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-[2.2rem] border border-gray-100 shadow-sm hover:shadow-md transition-all group relative overflow-hidden">
            <div className="flex items-center justify-between mb-6">
                <div className={`p-4 ${stat.bg} ${stat.color} rounded-2xl group-hover:scale-110 transition-transform`}>
                    <stat.icon size={24} />
                </div>
                <div className="flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border bg-green-50 text-green-600 border-green-100">
                    <TrendingUp size={10} />
                    {stat.growth}
                </div>
            </div>
            <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] leading-none mb-2">{stat.label}</p>
                <p className="text-3xl font-black text-gray-900 leading-none tracking-tight">{stat.value}</p>
            </div>
            <div className={`absolute -bottom-4 -left-4 w-12 h-12 ${stat.bg} opacity-20 rounded-full blur-xl`}></div>
          </div>
        ))}
      </div>

      {/* Detailed Insights Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Student Analytics Card */}
        <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 group hover:shadow-lg transition-all duration-500">
            <div className="flex items-center gap-3 mb-8 border-b border-gray-50 pb-6">
                <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center shadow-inner">
                    <Users size={24} />
                </div>
                <h2 className="text-xl font-black text-gray-900 tracking-tighter uppercase">إحصائيات الطلاب</h2>
            </div>
            <div className="space-y-4">
                {[
                    { label: 'الطلاب النشطون حالياً', value: statistics?.totalStudents || 0, icon: Zap, color: 'text-amber-500' },
                    { label: 'طلاب جدد خلال هذه الفترة', value: statistics?.totalQuestions || 0, icon: UserCheck, color: 'text-emerald-500' },
                    { label: 'متوسط وقت الدراسة / الطالب', value: `${statistics?.totalViews || 0} ساعة`, icon: Clock, color: 'text-blue-500' },
                    { label: 'معدل التفاعل العام', value: `${statistics?.lessonsWithViews || 0}%`, icon: MousePointerClick, color: 'text-purple-500' }
                ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-gray-50/50 rounded-2xl border border-transparent hover:border-gray-100 hover:bg-white transition-all">
                        <div className="flex items-center gap-3">
                            <item.icon size={16} className={item.color} />
                            <span className="text-sm font-bold text-gray-600">{item.label}</span>
                        </div>
                        <span className="text-base font-black text-gray-900">{item.value}</span>
                    </div>
                ))}
            </div>
        </div>

        {/* Course Performance Card */}
        <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 group hover:shadow-lg transition-all duration-500">
            <div className="flex items-center gap-3 mb-8 border-b border-gray-50 pb-6">
                <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center shadow-inner">
                    <BookOpen size={24} />
                </div>
                <h2 className="text-xl font-black text-gray-900 tracking-tighter uppercase">أداء المحتوى التعليمي</h2>
            </div>
            <div className="space-y-4">
                {[
                    { label: 'الكورسات المتاحة للنشر', value: statistics?.active_courses || 0, icon: CheckCircle2, color: 'text-emerald-500' },
                    { label: 'إجمالي الدروس المرفوعة', value: statistics?.total_lessons || 0, icon: Layers, color: 'text-blue-500' },
                    { label: 'متوسط مدة الكورس الواحد', value: `${statistics?.avg_course_duration || 0} ساعة`, icon: Timer, color: 'text-orange-500' },
                    { label: 'معدل إكمال المناهج', value: `${statistics?.lesson_completion_rate || 0}%`, icon: Target, color: 'text-rose-500' }
                ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-gray-50/50 rounded-2xl border border-transparent hover:border-gray-100 hover:bg-white transition-all">
                        <div className="flex items-center gap-3">
                            <item.icon size={16} className={item.color} />
                            <span className="text-sm font-bold text-gray-600">{item.label}</span>
                        </div>
                        <span className="text-base font-black text-gray-900">{item.value}</span>
                    </div>
                ))}
            </div>
        </div>

        {/* Support & Efficiency Card */}
        <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 group hover:shadow-lg transition-all duration-500">
            <div className="flex items-center gap-3 mb-8 border-b border-gray-50 pb-6">
                <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-2xl flex items-center justify-center shadow-inner">
                    <Zap size={24} />
                </div>
                <h2 className="text-xl font-black text-gray-900 tracking-tighter uppercase">جودة الرد والدعم</h2>
            </div>
            <div className="space-y-4">
                {[
                    { label: 'إجمالي الأسئلة المستلمة', value: statistics?.answeredQuestions || 0, icon: HelpCircle, color: 'text-blue-500' },
                    { label: 'الأسئلة التي تم حلها', value: statistics?.totalQuestions || 0, icon: CheckCircle2, color: 'text-emerald-500' },
                    { label: 'متوسط وقت الاستجابة', value: `${statistics?.avgResponseTime || 0} ساعة`, icon: Clock, color: 'text-amber-500' },
                    { label: 'نسبة رضا الطلاب', value: `${statistics?.studentSatisfaction || 0}%`, icon: Star, color: 'text-yellow-500' }
                ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-gray-50/50 rounded-2xl border border-transparent hover:border-gray-100 hover:bg-white transition-all">
                        <div className="flex items-center gap-3">
                            <item.icon size={16} className={item.color} />
                            <span className="text-sm font-bold text-gray-600">{item.label}</span>
                        </div>
                        <span className="text-base font-black text-gray-900">{item.value}</span>
                    </div>
                ))}
            </div>
        </div>

        {/* Time & Activity Distribution Card */}
        <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 group hover:shadow-lg transition-all duration-500">
            <div className="flex items-center gap-3 mb-8 border-b border-gray-50 pb-6">
                <div className="w-12 h-12 bg-rose-50 text-rose-600 rounded-2xl flex items-center justify-center shadow-inner">
                    <Calendar size={24} />
                </div>
                <h2 className="text-xl font-black text-gray-900 tracking-tighter uppercase">توزيع النشاط الزمني</h2>
            </div>
            <div className="space-y-4">
                {[
                    { label: 'ساعات التدريس (هذا الأسبوع)', value: statistics?.weekly_hours || 0, icon: Clock, color: 'text-emerald-500' },
                    { label: 'ساعات التدريس (هذا الشهر)', value: statistics?.monthly_hours || 0, icon: Calendar, color: 'text-blue-500' },
                    { label: 'متوسط الساعات اليومية', value: statistics?.avg_daily_hours || 0, icon: Timer, color: 'text-purple-500' },
                    { label: 'أكثر الأيام نشاطاً وإنتاجية', value: statistics?.most_active_day || 'غير محدد', icon: TrendingUp, color: 'text-rose-500' }
                ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-4 bg-gray-50/50 rounded-2xl border border-transparent hover:border-gray-100 hover:bg-white transition-all">
                        <div className="flex items-center gap-3">
                            <item.icon size={16} className={item.color} />
                            <span className="text-sm font-bold text-gray-600">{item.label}</span>
                        </div>
                        <span className="text-base font-black text-gray-900">{item.value}</span>
                    </div>
                ))}
            </div>
        </div>
      </div>

      {/* Modern Visual Charts Section */}
      <div className="bg-gray-900 rounded-[3rem] p-10 text-white relative overflow-hidden group shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/20 rounded-full blur-[100px] -z-0"></div>
        <div className="relative z-10 space-y-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="space-y-2">
                    <h2 className="text-3xl font-black flex items-center gap-3">
                        <LineChart className="text-primary-400" /> الرسوم البيانية للنمو
                    </h2>
                    <p className="text-gray-400 font-medium">تمثيل بصري دقيق لمعدلات التقدم والنمو خلال فترة التقرير</p>
                </div>
                <button className="bg-white text-gray-900 px-8 py-3 rounded-2xl font-black text-sm hover:bg-primary-50 transition-all transform active:scale-95 shadow-xl">
                    تصدير الرسوم البيانية
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="h-80 bg-white/5 border border-white/10 rounded-[2.5rem] flex items-center justify-center group/chart relative overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,#6366f1_1px,transparent_0)] bg-[size:30px_30px] opacity-10"></div>
                    <div className="text-center space-y-4">
                        <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto shadow-lg text-primary-400 group-hover/chart:rotate-12 transition-transform duration-500">
                            <BarChart3 size={32} />
                        </div>
                        <p className="font-black text-white text-lg">منحنى تقدم الدروس</p>
                        <p className="text-gray-500 text-xs uppercase tracking-widest">تحديث البيانات: فوري</p>
                    </div>
                </div>
                
                <div className="h-80 bg-white/5 border border-white/10 rounded-[2.5rem] flex items-center justify-center group/chart relative overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,#6366f1_1px,transparent_0)] bg-[size:30px_30px] opacity-10"></div>
                    <div className="text-center space-y-4">
                        <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto shadow-lg text-emerald-400 group-hover/chart:rotate-12 transition-transform duration-500">
                            <TrendingUp size={32} />
                        </div>
                        <p className="font-black text-white text-lg">معدل نمو الطلاب</p>
                        <p className="text-gray-500 text-xs uppercase tracking-widest">تحديث البيانات: فوري</p>
                    </div>
                </div>
            </div>
        </div>
      </div>

      {/* Call to Action Footer */}
      <div className="bg-primary-50 rounded-[2.5rem] p-10 border border-primary-100 text-center space-y-4">
        <div className="w-16 h-16 bg-white rounded-3xl shadow-lg flex items-center justify-center mx-auto mb-4 text-primary-600 transform -rotate-3">
            <Info size={32} />
        </div>
        <h3 className="text-2xl font-black text-gray-900">هل تود تحليل بيانات مخصصة؟</h3>
        <p className="text-gray-500 max-w-xl mx-auto font-medium">يمكنك التواصل مع فريق الدعم الفني لطلب تقارير متقدمة أو مخصصة لمواد معينة خارج نطاق الإحصائيات الحالية.</p>
        <div className="pt-4">
            <button className="bg-primary-600 text-white px-10 py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all transform active:scale-95">
                تواصل مع محلل البيانات
            </button>
        </div>
      </div>

    </div>
  );
};

// أيقونة إضافية
const UserCheck = ({ size, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><polyline points="17 11 19 13 23 9" />
    </svg>
);

export default TeacherStatistics;